import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { useSelector, useDispatch } from 'react-redux';
import { EditorState, ContentState } from 'draft-js';
import { useTranslation } from 'react-i18next';
import { CloudUploadRounded } from '@mui/icons-material';
import {
  checkFeaturePermission,
  removeEmptyLineOfText,
} from '@src/services/tts';
import { splitParagraphIntoSentence } from '@src/services/sentence';
import { FEATURE, PACKAGE_STUDIO_VERSION } from '@src/constants/package';
import { handleEditParagraphs } from '@src/services/paragraph';
import actions from '@src/redux/actions';
import { COLOR } from '@src/styles/color';
import Decorator from '@src/components/Decorator';
import { BREAK_TIME_REGEX, REGEX } from '@src/constants/voice';
import { TTS_GUIDE } from '@src/constants/tourGuide';
import { INPUT_TTS_TYPE } from '@src/constants/tts';
// import PopoverUpload from '@src/components/PopoverUpload';
import PopoverUploadNew from '@src/components/PopoverUploadNew';
import { OPTIONS_UPLOAD, TYPE_UPLOAD } from '@src/constants/upload';
import useUserInfo from '@src/hooks/useTtsUser';
import UploadFile from './UploadFile';
import { StyledImportToolbar } from './index.style';
import NewImportURL from './NewImportURL';

const ImportToolbarNew = ({
  useNewTtsUI,
  openSentences,
  onChangeContent,
  onChangeInputLength,
  onChangeCharacterExceed,
  handleCloseSearchBoxAndMoreMenu,
  handleCloseSearchBox,
  isMoreButton,
  newUiImportData,
}) => {
  const [openUpload, setOpenUpload] = useState(false);
  const [openImportUrl, setOpenImportUrl] = useState(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();

  const { voice } = useSelector((state) => state.synthesisRequest);

  const { usingPackage } = useSelector((state) => state.user);
  const { ttsUser } = useUserInfo();
  const { advanceFeature } = useSelector((state) => state.synthesisRequest);

  const maxLengthInputText = ttsUser?.maxLengthInputText || 0;

  const hasTtsByFile =
    ttsUser.id && checkFeaturePermission(ttsUser.features, FEATURE.TTS_BY_FILE);

  const hasTtsByUrl =
    ttsUser.id && checkFeaturePermission(ttsUser.features, FEATURE.TTS_BY_URL);

  const handleOnClick = () => {
    if (isMoreButton) handleCloseSearchBox();
    else handleCloseSearchBoxAndMoreMenu();
  };

  const handleOpenUpload = () => {
    if (!hasTtsByFile) {
      dispatch(actions.dialog.displayDialog({ limitedFeature: true }));
      return;
    }
    setOpenUpload(true);
  };
  const handleCloseUpload = () => setOpenUpload(false);

  const handleOpenImportUrl = () => {
    if (!hasTtsByUrl) {
      dispatch(actions.dialog.displayDialog({ limitedFeature: true }));
      return;
    }
    setOpenImportUrl(true);
  };
  const handleCloseImportUrl = () => setOpenImportUrl(false);

  const handleUploadContent = (text) => {
    const MAX_LENGTH_TEXT = 500000;
    const handledText = removeEmptyLineOfText(text);

    let textFormat = handledText;
    if (handledText.length > MAX_LENGTH_TEXT) {
      textFormat = handledText.slice(0, MAX_LENGTH_TEXT);
    }

    if (openSentences) {
      const regex = advanceFeature ? REGEX.ADVANCE_TAG : BREAK_TIME_REGEX;
      const newSentences = splitParagraphIntoSentence(textFormat, voice, regex);
      dispatch(actions.synthesisRequest.updateSentences(newSentences));
    } else {
      const contentEditor = EditorState.createWithContent(
        ContentState.createFromText(textFormat),
        Decorator,
      );
      const newEditorState = EditorState.moveFocusToEnd(contentEditor);
      onChangeContent(newEditorState);
      if (advanceFeature) {
        const newParagraphs = handleEditParagraphs([], newEditorState);
        dispatch(actions.synthesisRequest.updateParagraphs(newParagraphs));
      }
    }
    const textLength = textFormat.length;
    onChangeInputLength(textLength);

    if (textLength > maxLengthInputText) onChangeCharacterExceed(true);
    if (useNewTtsUI)
      dispatch(actions.synthesisRequest.cloneSynthesisRequest(true));

    // Scroll to top of editor when upload file or import url
    const elementEditor = document.getElementById('editor-wrapper');
    if (elementEditor) {
      elementEditor.scrollTop = 0;
    }
  };

  const handleSelectOptionUpload = (name, item) => {
    const { feature: featureName, featureDesc } = item;
    const hasFeature = checkFeaturePermission(ttsUser?.features, featureName);

    const isPackageV2 =
      usingPackage?.version === PACKAGE_STUDIO_VERSION.V2 ||
      usingPackage?.version === PACKAGE_STUDIO_VERSION.V2_1;

    // If user doesn't have feature and using package v2, show dialog to upgrade package
    // Otherwise, handle open upload or import url dialog
    if (!hasFeature && isPackageV2) {
      dispatch(
        actions.limitedFeaturesDialog.showLimitedFeaturesDialog({
          description: t('limitedFeatureDesc', { feature: t(featureDesc) }),
        }),
      );
      return;
    }

    if (item.type === TYPE_UPLOAD.LINK) {
      handleOpenImportUrl();
    } else {
      handleOpenUpload();
    }
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const inputType = queryParams.get('inputType');
    if (inputType === INPUT_TTS_TYPE.FILE) setOpenUpload(true);
    else if (inputType === INPUT_TTS_TYPE.LINK) setOpenImportUrl(true);
  }, [location]);

  return (
    <StyledImportToolbar id={TTS_GUIDE.UPLOAD_FILE_OR_IMPORT_URL}>
      <div id={TTS_GUIDE.UPLOAD_TEXT_FILE} className="upload">
        <div className={`${newUiImportData ? '' : 'margin-right'}`}>
          <PopoverUploadNew
            name="bitrate"
            tooltipTitle="tooltipUpload"
            icon={
              newUiImportData ? (
                <CloudUploadRounded
                  sx={{
                    fontSize: '24px',
                    color: COLOR.navyBlue,
                  }}
                />
              ) : (
                <CloudUploadRounded
                  sx={{
                    fontSize: 20,
                    opacity: 0.8,
                  }}
                />
              )
            }
            onClick={handleOnClick}
            options={OPTIONS_UPLOAD.filter((item) => item.isActive)}
            onSelect={handleSelectOptionUpload}
            title={newUiImportData ? '' : t('uploadFile')}
            hideArrowIcon={newUiImportData}
          />
        </div>
      </div>
      <UploadFile
        open={openUpload}
        onClose={handleCloseUpload}
        onHandleUploadContent={handleUploadContent}
      />
      <NewImportURL
        open={openImportUrl}
        onClose={handleCloseImportUrl}
        onHandleUploadContent={handleUploadContent}
      />
    </StyledImportToolbar>
  );
};

export default ImportToolbarNew;
